/**
 * Spotlight for the in-session chat sidebar (Evidence onboarding step 2/3).
 * Opens when the user clicks "Next" on the Session Ask Heidi spotlight (sidebar opens at the same time).
 * Structure and style match SessionAskHeidiSpotlight; uses session-chat-sidebar-spotlight-visual.svg.
 * On Next: opens sidebar (if needed), sends chat message, then Sources spotlight (step 3/3) opens after stream ends.
 * @see https://www.figma.com/design/PiRSH2ORtexkp82fSPpi9Z/Evidence-led-sign-up---onboarding?node-id=6664-24724
 */

import { IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED } from '@/app-shell/flags/evidence-flags';
import sessionChatSidebarSpotlightVisual from '@/assets/image/evidence/session-chat-sidebar-spotlight-visual.svg';
import { pendingChatMessageAtom } from '@/atoms/chatLaunchAtoms';
import {
  activeSidebarChatIdAtom,
  sidebarChatIdAtomFamily,
} from '@/atoms/chatSidebarAtoms';
import {
  pendingSourcesSpotlightAfterStreamAtom,
  sessionChatSidebarSpotlightOpenAtom,
} from '@/atoms/evidenceOnboardingAtoms';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/analytics/vocabulary';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightImage,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { SignupSource } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

/** Match ResizableMenuPanels panel expand animation so spotlight becomes visible after layout is ready */
const SPOTLIGHT_VISIBLE_AFTER_MS = 500;

interface SessionChatSidebarSpotlightProps {
  /** The close button (or other anchor). When spotlight is active it becomes the anchor. */
  children: ReactNode;
}

/**
 * Renders the spotlight anchored to the chat sidebar close button.
 * Use around the close button (e.g. in NonChatRouteActions). When conditions aren't met, just renders children.
 */
export const SessionChatSidebarSpotlight = ({
  children,
}: SessionChatSidebarSpotlightProps) => {
  const intl = useIntl();
  const sessionId = useCurrentSessionId();
  const [isOpen, setIsOpen] = useAtom(sessionChatSidebarSpotlightOpenAtom);
  // Defer showing spotlight until sidebar panel has expanded (avoids invisible anchor / wrong position)
  const [visibleOpen, setVisibleOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setVisibleOpen(false);
      return;
    }
    const timeoutId = window.setTimeout(
      () => setVisibleOpen(true),
      SPOTLIGHT_VISIBLE_AFTER_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    if (visibleOpen) {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.SessionChatSidebarSpotlight },
      });
    }
  }, [visibleOpen]);

  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId ?? ''));
  const setPendingMessage = useSetAtom(pendingChatMessageAtom);
  const setPendingSourcesSpotlightAfterStream = useSetAtom(
    pendingSourcesSpotlightAfterStreamAtom
  );
  const { data: signupSource } = useQueryUser((data) => data?.signup_source);

  const messageToSend = intl.formatMessage({
    defaultMessage:
      'What does the current evidence say about best practice for diagnosing and managing type 2 diabetes in adults?',
    id: '3+oNw4gRuz',
    description: 'Example Evidence question sent when the user continues onboarding',
  });

  const isFlowEnabled = IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();
  const shouldShowSpotlight = useMemo(
    () => isFlowEnabled && signupSource === SignupSource.scribe,
    [isFlowEnabled, signupSource]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
    },
    [setIsOpen]
  );

  const handleNextClick = useCallback(() => {
    if (messageToSend) {
      setActiveSidebarChatId(null);
      setSidebarChatId(null);
      setPendingMessage(messageToSend);
      setPendingSourcesSpotlightAfterStream(true);
    }
    setIsOpen(false);
  }, [
    messageToSend,
    setActiveSidebarChatId,
    setSidebarChatId,
    setPendingMessage,
    setPendingSourcesSpotlightAfterStream,
    setIsOpen,
  ]);

  if (!isFlowEnabled || !shouldShowSpotlight) {
    return <>{children}</>;
  }

  return (
    <Spotlight open={visibleOpen} onOpenChange={handleOpenChange}>
      <SpotlightAnchor asChild>{children}</SpotlightAnchor>
      <SpotlightContent
        align="start"
        alignOffset={-8}
        side="left"
        sideOffset={24}
        className="w-62 max-w-[calc(100vw-2rem)] rounded-xl border-0 p-0"
        data-testid="session-chat-sidebar-spotlight"
      >
        <SpotlightArrow />
        <SpotlightHeader className="rounded-t-xl px-4 pt-3">
          <SpotlightTitle className="flex-1">
            <FormattedMessage
              defaultMessage="Dig deeper, ask more"
              id="0DwFhQanfU"
              description="Title of the Evidence chat sidebar onboarding spotlight"
            />
          </SpotlightTitle>
          <SpotlightClose />
        </SpotlightHeader>
        <SpotlightImage className="relative h-[144px] w-full overflow-hidden bg-surface">
          <Image
            src={sessionChatSidebarSpotlightVisual}
            alt=""
            fill
            className="object-contain"
          />
        </SpotlightImage>
        <SpotlightFooter className="flex flex-col gap-3 rounded-b-xl bg-callout-background px-4 pb-3 text-text-primary-on-dark">
          <SpotlightDescription className="p-0 text-start">
            <FormattedMessage
              defaultMessage="Keep asking questions throughout your consultation. Start new Evidence chats whenever you need them."
              id="cPO8ecBSeN"
              description="Description of how to use the Evidence chat sidebar during a consultation"
            />
          </SpotlightDescription>
          <div className="flex w-full items-center justify-between">
            <SpotlightProgress>
              <FormattedMessage
                defaultMessage="{currentStep}/{totalSteps}"
                id="2mnIDSPn/d"
                values={{ currentStep: 2, totalSteps: 3 }}
                description="Current step and total steps in the Evidence onboarding walkthrough"
              />
            </SpotlightProgress>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleNextClick}
              className="bg-surface text-text-primary hover:bg-background-secondary"
            >
              <FormattedMessage
                defaultMessage="Next"
                id="0nuDfMr2nl"
                description="Button to continue the Evidence onboarding walkthrough"
              />
            </Button>
          </div>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
